// PD Activity Categories - matches Prisma enum
export type PDActivityCategory =
  | 'COURSE'
  | 'WORKSHOP'
  | 'CONFERENCE'
  | 'WEBINAR'
  | 'READING'
  | 'RESEARCH'
  | 'TEACHING'
  | 'PEER_CONSULTATION'
  | 'CLINICAL_SUPERVISION'
  | 'PERSONAL_THERAPY'
  | 'STUDY_GROUP'
  | 'OTHER';

// Activity types - Didactic (instruction-based) vs Experiential (experience-based)
export type PDActivityType = 'DIDACTIC' | 'EXPERIENTIAL';

export type PDActivityStatus = 'DRAFT' | 'COMPLETED';

// Category metadata
export interface CategoryInfo {
  label: string;
  description: string;
  type: PDActivityType;
}

export const CATEGORY_INFO: Record<PDActivityCategory, CategoryInfo> = {
  // Didactic (instruction-based)
  COURSE: {
    label: 'Course',
    description: 'Formal courses (online or in-person)',
    type: 'DIDACTIC',
  },
  WORKSHOP: {
    label: 'Workshop',
    description: 'Workshops and seminars',
    type: 'DIDACTIC',
  },
  CONFERENCE: {
    label: 'Conference',
    description: 'Conferences and conventions',
    type: 'DIDACTIC',
  },
  WEBINAR: {
    label: 'Webinar',
    description: 'Online webinars',
    type: 'DIDACTIC',
  },
  READING: {
    label: 'Reading',
    description: 'Professional reading (books, articles, journals)',
    type: 'DIDACTIC',
  },
  RESEARCH: {
    label: 'Research',
    description: 'Research activities',
    type: 'DIDACTIC',
  },
  TEACHING: {
    label: 'Teaching/Presenting',
    description: 'Teaching or presenting at events',
    type: 'DIDACTIC',
  },
  // Experiential (experience-based)
  PEER_CONSULTATION: {
    label: 'Peer Consultation',
    description: 'Peer consultation groups',
    type: 'EXPERIENTIAL',
  },
  CLINICAL_SUPERVISION: {
    label: 'Clinical Supervision',
    description: 'Additional clinical supervision',
    type: 'EXPERIENTIAL',
  },
  PERSONAL_THERAPY: {
    label: 'Personal Therapy',
    description: 'Personal psychotherapy',
    type: 'EXPERIENTIAL',
  },
  STUDY_GROUP: {
    label: 'Study Group',
    description: 'Study groups',
    type: 'EXPERIENTIAL',
  },
  OTHER: {
    label: 'Other',
    description: 'Other qualifying activities',
    type: 'DIDACTIC', // Default to didactic, user should specify
  },
};

// Get categories grouped by type
export const DIDACTIC_CATEGORIES: PDActivityCategory[] = [
  'COURSE',
  'WORKSHOP',
  'CONFERENCE',
  'WEBINAR',
  'READING',
  'RESEARCH',
  'TEACHING',
];

export const EXPERIENTIAL_CATEGORIES: PDActivityCategory[] = [
  'PEER_CONSULTATION',
  'CLINICAL_SUPERVISION',
  'PERSONAL_THERAPY',
  'STUDY_GROUP',
];

// Supervision format type (for supervision-sourced activities)
export type SupervisionFormat = 'INDIVIDUAL' | 'DYAD' | 'GROUP' | 'PEER';

// PD Activity interface
export interface PDActivity {
  id: string;
  proId: string;
  title: string;
  description?: string | null;
  category: PDActivityCategory;
  activityType: PDActivityType;
  activityDate: string; // ISO date string
  duration: number; // Minutes
  provider?: string | null;
  certificateUrl?: string | null;
  certificateFileName?: string | null;
  learningObjectives?: string | null;
  keyTakeaways?: string | null;
  applicationPlan?: string | null;
  status: PDActivityStatus;
  createdAt: string;
  updatedAt: string;

  // Supervision source fields (for auto-logged activities)
  isFromSupervision?: boolean;
  supervisionFormat?: SupervisionFormat;
  supervisorName?: string | null;
  supervisorCredentials?: string | null;
}

// Form data for creating/editing activities
export interface PDActivityFormData {
  title: string;
  description?: string;
  category: PDActivityCategory;
  activityDate: string;
  durationHours: number;
  durationMinutes: number;
  provider?: string;
  certificateFile?: File | null;
  learningObjectives?: string;
  keyTakeaways?: string;
  applicationPlan?: string;
}

// PD Cycle information
export interface PDCycle {
  start: Date;
  end: Date;
  isFirstCycle: boolean;
}

// Statistics for the tracker
export interface PDStats {
  cycleStartDate: string;
  cycleEndDate: string;
  totalHours: number;
  targetHours: number; // Always 40
  hoursRemaining: number;
  hasDidactic: boolean;
  hasExperiential: boolean;
  byCategory: Record<PDActivityCategory, number>;
  byType: {
    didactic: number;
    experiential: number;
  };
  activityCount: number;
  daysRemaining: number;
  isFirstCycle: boolean;
}

// Props for the main tracker component
export interface PDTrackerProps {
  proId: string;
  crpoRegistrationDate?: string | null;
  apiEndpoint?: string;
  profileApiEndpoint?: string; // Endpoint to save registration date
  onActivityAdded?: () => void;
  onActivityUpdated?: () => void;
  onActivityDeleted?: () => void;
  onRegistrationDateSaved?: (date: string) => void;
}

// Props for user context
export interface PDUser {
  id: string;
  firstName: string;
  email: string;
  crpoRegistrationDate?: string | null;
}

// CRPO Requirements
export const PD_REQUIREMENTS = {
  totalHours: 40, // 40 hours per 2-year cycle
  requiresDidactic: true, // At least one didactic activity
  requiresExperiential: true, // At least one experiential activity
  cycleLengthYears: 2,
} as const;

// Valid categories for API validation (same as PDActivityCategory type values)
export const VALID_CATEGORIES: PDActivityCategory[] = [
  'COURSE', 'WORKSHOP', 'CONFERENCE', 'WEBINAR', 'READING', 'RESEARCH', 'TEACHING',
  'PEER_CONSULTATION', 'CLINICAL_SUPERVISION', 'PERSONAL_THERAPY', 'STUDY_GROUP', 'OTHER'
];

// Map category to activity type for API use
export const CATEGORY_TYPE_MAP: Record<PDActivityCategory, PDActivityType> = {
  COURSE: 'DIDACTIC',
  WORKSHOP: 'DIDACTIC',
  CONFERENCE: 'DIDACTIC',
  WEBINAR: 'DIDACTIC',
  READING: 'DIDACTIC',
  RESEARCH: 'DIDACTIC',
  TEACHING: 'DIDACTIC',
  PEER_CONSULTATION: 'EXPERIENTIAL',
  CLINICAL_SUPERVISION: 'EXPERIENTIAL',
  PERSONAL_THERAPY: 'EXPERIENTIAL',
  STUDY_GROUP: 'EXPERIENTIAL',
  OTHER: 'DIDACTIC',
};
